import EditorLayout from "./Layout/EditorLayout";
import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { ViewQuiltIcon as Layout, CalendarIcon, ImageIcon, SaveIcon as Save, CloseIcon as X, EyeIcon as Eye, TextFieldsIcon as Type, ListIcon as List, GridIcon as Grid, MailIcon as Mail, CropIcon, AlertIcon as AlertTriangle, ExternalLinkIcon as ExternalLink, RefreshIcon as RefreshCw } from './Icons';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/cropImage';
import EventPreview from './EventPreview';
import UserPermissionSelector from './UserPermissionSelector';

const HELPFUL_TIPS = [
    "Nutze verschiedene Sektionen, um deine Seite strukturiert aufzubauen.",
    "Bilder lockern den Text auf und machen die Seite ansprechender.",
    "Eine klare Überschrift hilft Nutzern, das Thema schnell zu erfassen.",
    "Verwende Listen für Aufzählungen oder Checklisten.",
    "Die 'Infos'-Box eignet sich gut für wichtige Hinweise."
];

const EventPageBuilder = ({ event, allUsers = [], onUpdate, onClose }) => {
    const { user, API_URL } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // 'general', 'content', 'email'
    const [showPreview, setShowPreview] = useState(false); // Mobile preview toggle
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(!!event.slug);

    // Helper: Slugify title
    const slugify = (text) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .replace(/[^a-z0-9 -]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    // --- Basic Data State (General Tab) ---
    const [basicData, setBasicData] = useState({
        title: event.title || '',
        subtitle: event.pageContent?.hero?.subtitle || '',
        date: event.date ? event.date.split('T')[0] : '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        location: event.location || '',
        description: event.description || '',
        category: event.category || 'Sonstiges',
        link: event.link || '',
        requiresConsent: event.requiresConsent || false,
        isRegistrationEnabled: event.isRegistrationEnabled || false,
        editableBy: event.editableBy?.map(u => u._id || u) || [],
        status: event.status || 'published', // Default to published if undefined for legacy
        slug: event.slug || ''
    });

    // --- Page Content State ---
    const [pageData, setPageData] = useState({
        hero: {
            title: event.pageContent?.hero?.title || event.title || '',
            subtitle: event.pageContent?.hero?.subtitle || '',
            imageUrl: event.pageContent?.hero?.imageUrl || event.imageUrl || '',
            overlayText: event.pageContent?.hero?.overlayText || ''
        },
        sections: event.pageContent?.sections || []
    });

    // --- Email Template State ---
    const [registrationEmailTemplate, setRegistrationEmailTemplate] = useState({
        subject: event.registrationEmailTemplate?.subject || `Anmeldung bestätigt: ${event.title}`,
        body: event.registrationEmailTemplate?.bodyTemplate || event.emailTemplate?.body || `Hallo {"{{name}}"},\n\ndeine Anmeldung für "${event.title}" wurde erfolgreich bestätigt.\n\nWir freuen uns auf dich!\n\nViele Grüße,\nDein Pfadfinder-Team`
    });

    // --- Image Upload State ---
    const [showImageUpload, setShowImageUpload] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [targetImageSectionIndex, setTargetImageSectionIndex] = useState(null); // null for hero, index for sections

    // --- UI Helpers ---
    const [currentTip, setCurrentTip] = useState(HELPFUL_TIPS[0]);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTip(HELPFUL_TIPS[Math.floor(Math.random() * HELPFUL_TIPS.length)]);
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    // --- Handlers ---

    // 1. Basic Data Handlers
    const handleBasicDataChange = (field, value) => {
        setBasicData(prev => {
            const newData = { ...prev, [field]: value };
            
            // Sync title changes with hero title and auto-slug
            if (field === 'title') {
                setPageData(content => ({ ...content, hero: { ...content.hero, title: value } }));
                if (!slugManuallyEdited) {
                    newData.slug = slugify(value);
                }
            }

            // Mark slug as manually edited
            if (field === 'slug') {
                setSlugManuallyEdited(true);
            }

            return newData;
        });
    };

    const toggleUserPermission = (userId) => {
        setBasicData(prev => {
            const current = prev.editableBy || [];
            if (current.includes(userId)) {
                return { ...prev, editableBy: current.filter(id => id !== userId) };
            } else {
                return { ...prev, editableBy: [...current, userId] };
            }
        });
    };

    // 2. Content Handlers
    const updateHero = (field, value) => {
        setPageData(prev => ({
            ...prev,
            hero: { ...prev.hero, [field]: value }
        }));
    };

    const addSection = (type) => {
        const newSection = {
            type,
            title: '',
            content: '',
            items: type === 'list' || type === 'grid' ? [''] : undefined, // Initialize with one item for lists
            iconItems: type === 'icon-list' ? [{ icon: 'Check', text: '', iconColor: 'bg-primary-container text-primary-onContainer' }] : undefined,
            imageUrl: type === 'image' ? '' : undefined,
            imageSize: type === 'image' ? 'medium' : undefined // small, medium, large, full
        };
        setPageData(prev => ({
            ...prev,
            sections: [...prev.sections, newSection]
        }));
    };

    const updateSection = (index, field, value) => {
        const newSections = [...pageData.sections];
        newSections[index] = { ...newSections[index], [field]: value };
        setPageData(prev => ({ ...prev, sections: newSections }));
    };

    const removeSection = (index) => {
        setPageData(prev => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== index)
        }));
    };

    const moveSection = (index, direction) => {
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === pageData.sections.length - 1)) return;
        const newSections = [...pageData.sections];
        const temp = newSections[index];
        newSections[index] = newSections[index + (direction === 'up' ? -1 : 1)];
        newSections[index + (direction === 'up' ? -1 : 1)] = temp;
        setPageData(prev => ({ ...prev, sections: newSections }));
    };

    // 3. Image Upload Logic
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const initiateImageUpload = (sectionIndex = null) => {
        setTargetImageSectionIndex(sectionIndex);
        setShowImageUpload(true);
    };

    const handleFileChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => setImageSrc(reader.result));
            reader.readAsDataURL(file);
        }
    };

    const uploadCroppedImage = async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        setUploadingImage(true);
        try {
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const formData = new FormData();
            formData.append('file', croppedImageBlob, 'event-image.jpg');

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`,
                },
            };

            const response = await axios.post(`${API_URL}/files/upload`, formData, config);
            const imageUrl = response.data.url; // Correct property name from backend

            if (targetImageSectionIndex === null) {
                // Hero Image
                updateHero('imageUrl', imageUrl);
            } else {
                // Section Image
                updateSection(targetImageSectionIndex, 'imageUrl', imageUrl);
            }

            setShowImageUpload(false);
            setImageSrc(null);
        } catch (error) {
            console.error('Upload failed', error);
            alert('Bildupload fehlgeschlagen.');
        } finally {
            setUploadingImage(false);
        }
    };


    // 4. Save Logic
    const validateEventData = () => {
        if (!basicData.title || basicData.title.trim() === '') {
            return "Bitte gib einen Titel für das Event ein.";
        }
        if (!basicData.date) {
            return "Bitte wähle ein Datum für das Event.";
        }
        if (basicData.startTime && basicData.endTime) {
            if (basicData.startTime > basicData.endTime) {
                return "Die Endzeit kann nicht vor der Startzeit liegen.";
            }
        }
        return null; // Valid
    };

    const handleSave = async () => {
        const errorMsg = validateEventData();
        if (errorMsg) {
            alert(errorMsg);
            return;
        }

        setLoading(true);
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };

            // Combine all data
            const completeEventData = {
                ...basicData, // Title, date, editableBy, etc.
                pageContent: pageData,
                registrationEmailTemplate: {
                    subject: registrationEmailTemplate.subject,
                    bodyTemplate: registrationEmailTemplate.body
                }
            };

            // If updating an existing event, preserve ID and other system fields handled by backend
            // But here we are specifically updating via a dedicated endpoint or the general event update
            // Using the general update endpoint as established in EventManagement.js
            await axios.put(`${API_URL}/events/${event._id}`, completeEventData, config);

            if (onUpdate) onUpdate();
            alert('Event-Seite erfolgreich gespeichert!');
        } catch (err) {
            console.error("Error saving event page:", err);
            alert("Fehler beim Speichern der Event-Seite: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <EditorLayout
            title="Event-Seite gestalten"
            subtitle={basicData.title}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={[
                { id: "general", label: "Allgemein" },
                { id: "content", label: "Seiten-Inhalt" },
                { id: "email", label: "E-Mail Bestätigung" }
            ]}
            actions={
                <>
                    <button
                        onClick={() => handleBasicDataChange('status', basicData.status === 'published' ? 'draft' : 'published')}
                        className={`md3-btn-tonal ${basicData.status === 'published' ? 'bg-error/10 text-error hover:bg-error/20' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}
                    >
                        {basicData.status === 'published' ? 'Zurückziehen' : 'Veröffentlichen'}
                    </button>
                    <button onClick={onClose} className="md3-btn-text">Abbrechen</button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="md3-btn-filled flex items-center gap-2"
                    >
                        {loading ? <RefreshCw className="animate-spin w-4 h-4" /> : <Save sx={{ fontSize: 18 }} />}
                        <span>Speichern</span>
                    </button>
                </>
            }
            sidebar={
                <div className="space-y-6">
                    <div className="flex items-center gap-2 text-title-medium font-bold text-surface-onVariant mb-4">
                        <Eye size={20} />
                        <span>Vorschau</span>
                    </div>
                    <div className="scale-75 xl:scale-90 origin-top">
                        <EventPreview
                            event={{ ...basicData, pageContent: pageData }}
                            hideRegisterButton={true}
                        />
                    </div>
                </div>
            }
        >
            <div className="flex-1 overflow-y-auto p-1">
                {activeTab === 'general' && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                            <h3 className="text-title-large font-bold mb-6 text-surface-on">Basisdaten</h3>
                            <div className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-label-large text-surface-onVariant ml-1 font-bold">Event Titel</label>
                                    <input
                                        type="text"
                                        value={basicData.title}
                                        onChange={(e) => handleBasicDataChange('title', e.target.value)}
                                        className="md3-textfield-outlined"
                                        placeholder="Titel des Events"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-label-large text-surface-onVariant ml-1 font-bold">URL-Slug (e.g. sommer-lager-2026)</label>
                                    <input
                                        type="text"
                                        value={basicData.slug}
                                        onChange={(e) => handleBasicDataChange('slug', e.target.value)}
                                        className="md3-textfield-outlined font-mono text-sm"
                                        placeholder="mein-event-url"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-label-large text-surface-onVariant ml-1 font-bold">Untertitel / Kurzbeschreibung</label>
                                    <textarea
                                        value={basicData.subtitle}
                                        onChange={(e) => handleBasicDataChange('subtitle', e.target.value)}
                                        rows={2}
                                        className="md3-textfield-outlined py-3"
                                        placeholder="Kurzer, einladender Text"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                                <h3 className="text-title-medium font-bold mb-4 flex items-center gap-2">
                                    <CalendarIcon sx={{ fontSize: 20 }} /> Zeit & Ort
                                </h3>
                                <div className="space-y-4">
                                    <input
                                        type="date"
                                        value={basicData.date}
                                        onChange={(e) => handleBasicDataChange('date', e.target.value)}
                                        className="md3-textfield-outlined"
                                    />
                                    <div className="flex gap-4">
                                        <input
                                            type="time"
                                            value={basicData.startTime}
                                            onChange={(e) => handleBasicDataChange('startTime', e.target.value)}
                                            className="md3-textfield-outlined flex-1"
                                        />
                                        <input
                                            type="time"
                                            value={basicData.endTime}
                                            onChange={(e) => handleBasicDataChange('endTime', e.target.value)}
                                            className="md3-textfield-outlined flex-1"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        value={basicData.location}
                                        onChange={(e) => handleBasicDataChange('location', e.target.value)}
                                        placeholder="Ort"
                                        className="md3-textfield-outlined"
                                    />
                                </div>
                            </div>

                            <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                                <h3 className="text-title-medium font-bold mb-4 flex items-center gap-2">
                                    <Layout sx={{ fontSize: 20 }} /> Kategorie & Sichtbarkeit
                                </h3>
                                <div className="space-y-4">
                                    <select
                                        value={basicData.category}
                                        onChange={(e) => handleBasicDataChange('category', e.target.value)}
                                        className="md3-textfield-outlined h-14"
                                    >
                                        <option value="Gruppenstunde">Gruppenstunde</option>
                                        <option value="Lager">Lager</option>
                                        <option value="Aktion">Aktion</option>
                                        <option value="Sonstiges">Sonstiges</option>
                                    </select>

                                    <div className="flex items-center justify-between p-3 bg-surface-container rounded-xl">
                                        <span className="text-label-large font-bold">Anmeldung aktiviert</span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={basicData.isRegistrationEnabled}
                                                onChange={(e) => handleBasicDataChange('isRegistrationEnabled', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-outlineVariant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'content' && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                            <h3 className="text-title-large font-bold mb-6 text-surface-on">Hero-Bereich</h3>
                            <div className="space-y-4">
                                <input
                                    type="text"
                                    value={pageData.hero.imageUrl}
                                    onChange={(e) => setPageData(prev => ({ ...prev, hero: { ...prev.hero, imageUrl: e.target.value } }))}
                                    placeholder="Bild-URL"
                                    className="md3-textfield-outlined"
                                />
                                <button
                                    onClick={() => initiateImageUpload(null)}
                                    className="md3-btn-tonal w-full"
                                >
                                    <ImageIcon sx={{ fontSize: 18 }} className="mr-2" /> Bild hochladen & zuschneiden
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-title-large font-bold text-surface-on">Sektionen</h3>
                                <div className="flex gap-2">
                                    <button onClick={() => addSection('text')} className="md3-btn-tonal text-xs"><Type size={16} className="mr-1"/> Text</button>
                                    <button onClick={() => addSection('list')} className="md3-btn-tonal text-xs"><List size={16} className="mr-1"/> Liste</button>
                                    <button onClick={() => addSection('grid')} className="md3-btn-tonal text-xs"><Grid size={16} className="mr-1"/> Grid</button>
                                    <button onClick={() => addSection('image')} className="md3-btn-tonal text-xs"><ImageIcon size={16} className="mr-1"/> Bild</button>
                                </div>
                            </div>

                            {pageData.sections.length === 0 ? (
                                <div className="p-12 text-center border-2 border-dashed border-outline rounded-3xl opacity-50">
                                    Noch keine Sektionen hinzugefügt.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {pageData.sections.map((section, index) => (
                                        <div key={index} className="md3-card-outlined p-4 !bg-surface-containerLow group relative">
                                            <div className="flex items-center justify-between mb-4">
                                                <span className="text-label-small font-bold uppercase tracking-widest opacity-50">{section.type}</span>
                                                <div className="flex gap-1">
                                                    <button onClick={() => moveSection(index, 'up')} className="md3-icon-btn h-8 w-8"><X size={16} className="rotate-90" /></button>
                                                    <button onClick={() => moveSection(index, 'down')} className="md3-icon-btn h-8 w-8"><X size={16} className="-rotate-90" /></button>
                                                    <button onClick={() => removeSection(index)} className="md3-icon-btn h-8 w-8 text-error"><X size={18} /></button>
                                                </div>
                                            </div>
                                            {section.type === 'text' ? (
                                                <textarea
                                                    value={section.content}
                                                    onChange={(e) => updateSection(index, 'content', e.target.value)}
                                                    className="md3-textfield-outlined min-h-[100px]"
                                                    placeholder="Textinhalt..."
                                                />
                                            ) : (
                                                <div className="p-4 bg-surface-container rounded-xl italic text-surface-onVariant text-sm">
                                                    Inhalt für {section.type} bearbeiten...
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'email' && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                            <h3 className="text-title-large font-bold mb-6 text-surface-on">Bestätigungs-E-Mail</h3>
                            <div className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-label-large text-surface-onVariant ml-1 font-bold">Betreff</label>
                                    <input
                                        type="text"
                                        value={registrationEmailTemplate.subject}
                                        onChange={(e) => setRegistrationEmailTemplate(prev => ({ ...prev, subject: e.target.value }))}
                                        className="md3-textfield-outlined"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-label-large text-surface-onVariant ml-1 font-bold">Nachricht</label>
                                    <textarea
                                        value={registrationEmailTemplate.body}
                                        onChange={(e) => setRegistrationEmailTemplate(prev => ({ ...prev, body: e.target.value }))}
                                        rows={10}
                                        className="md3-textfield-outlined py-4 leading-relaxed"
                                    />
                                    <p className="text-body-small text-surface-onVariant italic ml-1">Verfügbare Platzhalter: {"{{name}}"}, {"{{eventTitle}}"}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Image Upload Modal overlay */}
            {showImageUpload && (
                <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col animate-fade-in">
                    <div className="flex justify-between items-center p-6 text-white border-b border-white/10">
                        <h3 className="text-title-large font-bold">Bild hochladen & zuschneiden</h3>
                        <button onClick={() => setShowImageUpload(false)} className="w-12 h-12 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
                            <X size={28} />
                        </button>
                    </div>

                    <div className="flex-1 relative">
                        {imageSrc ? (
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={16 / 9}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center gap-6">
                                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center text-white/50">
                                    <ImageIcon size={48} />
                                </div>
                                <label className="md3-btn-filled bg-white text-black cursor-pointer shadow-xl scale-110">
                                    Bild vom Computer wählen
                                    <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                </label>
                                <p className="text-white/40 text-sm">Optimale Auflösung: 1920x1080px</p>
                            </div>
                        )}
                    </div>

                    {imageSrc && (
                        <div className="p-8 flex items-center justify-between bg-black/50 backdrop-blur-md border-t border-white/10">
                            <div className="flex items-center gap-6">
                                <div className="flex flex-col gap-2 min-w-[200px]">
                                    <label className="text-label-small text-white/60 font-bold uppercase tracking-wider">Zoom</label>
                                    <input
                                        type="range"
                                        value={zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                                        className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-white"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => setImageSrc(null)} className="md3-btn-text text-white">Anderes Bild</button>
                                <button
                                    onClick={uploadCroppedImage}
                                    disabled={uploadingImage}
                                    className="md3-btn-filled !bg-white !text-black px-12 h-14"
                                >
                                    {uploadingImage ? <RefreshCw className="animate-spin" /> : 'Zuschneiden & Speichern'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </EditorLayout>
    );
};

export default EventPageBuilder;
